import { SxProps, Theme } from '@mui/system';
import { PDFPageProxy } from 'pdfjs-dist';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
import React, { useEffect, useState } from 'react';
import { debounce } from 'throttle-debounce';
import { DeviceType, useBreakpoint } from '../../../hooks/useBreakpoint';
import { StyledDocumentBodyBox } from '../CfDocumentViewer.styles';
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

interface CfDocumentViewerBodyProps {
  fileSrc: string;
  fileName: string;
  fileType?: string;
  containerSx?: SxProps<Theme>;
}

const CfDocumentViewerBody: React.FC<CfDocumentViewerBodyProps> = (props: CfDocumentViewerBodyProps) => {
  const { fileSrc, fileName, fileType, containerSx } = props;
  const [wrapperElem, setWrapperElem] = useState<HTMLDivElement | null>(null);
  const [baseScale, setBaseScale] = useState<number | null>(null);
  const { deviceType } = useBreakpoint();
  const renderPdf = async () => {
    const renderPage = async (page: PDFPageProxy) => {
      if (!wrapperElem) return;

      const canvas = document.createElement('canvas');

      let viewport = page.getViewport({ scale: 1 });

      // Subtract padding to avoid off‑by‑a‑few‑pixels issues
      const availableWidth =
        wrapperElem.clientWidth -
        (parseFloat(getComputedStyle(wrapperElem).paddingLeft) || 0) -
        (parseFloat(getComputedStyle(wrapperElem).paddingRight) || 0);

      // Safety margin to accommodate scrollbar on desktop
      const SAFETY_MARGIN = deviceType === DeviceType.Desktop ? 0.98 : 1;

      const fitScale = (availableWidth / viewport.width) * SAFETY_MARGIN;

      if (baseScale === null) {
        setBaseScale(fitScale);
      }

      const finalScale = baseScale ?? fitScale;

      viewport = page.getViewport({ scale: finalScale });

      const devicePixelRatio = window.devicePixelRatio || 1;
      const transform = devicePixelRatio == 1 ? undefined : [devicePixelRatio, 0, 0, devicePixelRatio, 0, 0];

      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
      const renderContext = {
        canvasContext: ctx,
        viewport: viewport,
        transform: transform,
      };

      const layoutWidth = Math.floor(viewport.width);
      const layoutHeight = Math.floor(viewport.height);

      canvas.style.width = layoutWidth + 'px';
      canvas.style.height = layoutHeight + 'px';
      canvas.width = layoutWidth * devicePixelRatio;
      canvas.height = layoutHeight * devicePixelRatio;
      wrapperElem.style.overflowY = 'scroll';
      wrapperElem.style.overflowX = 'auto';

      wrapperElem.appendChild(canvas);

      page.render(renderContext);
    };
    const renderPages = async () => {
      if (!wrapperElem) return;
      const loadingTask = pdfjsLib.getDocument(fileSrc);
      const pdf = await loadingTask.promise;
      wrapperElem.innerHTML = '';
      for (let i = 1; i < pdf.numPages + 1; i++) {
        pdf.getPage(i).then(renderPage);
      }
    };
    renderPages();
  };

  const debouncedRenderPdf = debounce(500, true, renderPdf);
  useEffect(() => {
    return debouncedRenderPdf.cancel;
  }, []);

  useEffect(() => {
    if (!wrapperElem) return;
    fileType === 'application/pdf' && debouncedRenderPdf();
  }, [fileSrc, deviceType, wrapperElem]);

  return (
    <StyledDocumentBodyBox sx={{ ...containerSx }}>
      {fileType === 'application/pdf' ? (
        <>
          <div ref={setWrapperElem}></div>
          <iframe
            id='documentObject'
            name='documentIframe'
            data-document-viewer
            src={`${fileSrc}#view=fitH`}
            title={fileName}
            style={{ display: 'none' }}
          >
            This browser does not support this document type. Please download the document to view it:
          </iframe>
        </>
      ) : (
        <iframe
          id='documentObject'
          name='documentIframe'
          data-document-viewer
          src={`${fileSrc}#view=fitH`}
          title={fileName}
          height='100%'
          width='100%'
        >
          This browser does not support this document type. Please download the document to view it:
        </iframe>
      )}
    </StyledDocumentBodyBox>
  );
};

export default CfDocumentViewerBody;
